import { FirebaseApp } from 'firebase/app';
import { hasAnalyticsConsent } from '../entities/analytics-consent.storage';
import { ANALYTICS_FIREBASE_APP, APP_ANALYTICS, FIREBASE_ANALYTICS } from '../tokens';
import { AnalyticsService } from '../services/analytics.service';

/**
 * Providers present whatever happens. `APP_ANALYTICS` is always bound so callers
 * can inject it unconditionally — with no Analytics instance behind it the
 * service simply no-ops. `ANALYTICS_FIREBASE_APP` is a value that already
 * exists, so holding it initialises nothing; it lets the service attach
 * Analytics the moment someone accepts, rather than after a reload.
 */
const baseProviders = (app: FirebaseApp) => [
  { provide: ANALYTICS_FIREBASE_APP, useValue: app },
  { provide: APP_ANALYTICS, useClass: AnalyticsService },
];

async function loadAnalyticsAfterConsent(app: FirebaseApp) {
  // Was `const consentGiven = true`, which made the guard below dead code and
  // initialised Firebase Analytics for everyone — including anyone who had
  // revoked, since the stored value was never consulted. Nobody has answered on
  // a first visit, so analytics now stays off until the consent banner gets a
  // yes.
  if (!hasAnalyticsConsent()) return baseProviders(app);

  try {
    const { isSupported, getAnalytics } = await import('firebase/analytics');

    if (!(await isSupported())) return baseProviders(app);

    return [
      { provide: FIREBASE_ANALYTICS, useValue: getAnalytics(app) },
      ...baseProviders(app),
    ];
  } catch (error) {
    console.error('Error loading analytics:', error);
    return baseProviders(app);
  }
}

export const provideFirebaseAnalytics = async (app: FirebaseApp) =>
  (await loadAnalyticsAfterConsent(app)) ?? [];
